import { useState, useEffect } from "react";
import "@/App.css";
import axios from "axios";
import { motion } from "framer-motion";
import Marquee from "react-fast-marquee";
import { Phone, Mail, MapPin, Menu, X, Clock, Heart, ArrowRight, Check, Facebook, Instagram, Star, Quote } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Social Media Links
const SOCIAL_LINKS = {
  facebook: "https://www.facebook.com/profile.php?id=61571742595355",
  instagram: "https://www.instagram.com/graceinmotionrehab",
};

// Assets
const LOGO = "https://customer-assets.emergentagent.com/job_grace-rehab-nacog/artifacts/2sn58ph4_GIM%20LOGO%20IVORY.jpg";
const HERO_IMAGE = "https://customer-assets.emergentagent.com/job_grace-rehab-nacog/artifacts/q48lj9gz_GM-136.JPG";
const WHO_WE_ARE_IMAGE = "https://customer-assets.emergentagent.com/job_grace-rehab-nacog/artifacts/u2n9b4nx_GM-177.JPG";

// Service images
const OT_IMAGE = "https://images.unsplash.com/photo-1645005512788-71110dc23393?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=800";
const PT_IMAGE = "https://images.unsplash.com/photo-1746806942507-a7e93fdd6dd4?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=800";
const ST_IMAGE = "https://customer-assets.emergentagent.com/job_grace-rehab-nacog/artifacts/zlq603cj_GM-206.JPG";

// OT highlight images
const BUILDING_SKILLS_IMAGE = "https://images.unsplash.com/photo-1503676260728-1c00da094a0b?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=800";
const CHRONIC_PAIN_IMAGE = "https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=800";

// Team photos - placeholder images (to be replaced with actual team photos)
const TEAM_IMAGES = {
  celina: "https://graceinmotionrehab.com/wp-content/uploads/2025/02/Celina-Headshot-scaled.jpg",
  marcy: "https://images.unsplash.com/photo-1559839734-2b71ea197ec2?w=400&h=400&fit=crop",
  sherrie: "https://graceinmotionrehab.com/wp-content/uploads/2025/02/Physical-Therapy-Peds-scaled.jpg",
  railey: "https://images.unsplash.com/photo-1594824476967-48c8b964273f?w=400&h=400&fit=crop",
  karen: "https://images.unsplash.com/photo-1580489944761-15a19d654956?w=400&h=400&fit=crop",
};

// Team data - Only OT staff for now
const teamMembers = [
  {
    id: "celina",
    name: "Celina Hawthorne, OTR, OTD",
    title: "Occupational Therapist",
    bio: "With 25 years of experience, Celina provides exceptional, client-centered care grounded in true partnership with each individual she serves. Her work is guided by a deep commitment to servant leadership, approaching each interaction with compassion, purpose, and a desire to reflect Christ through the care she provides.",
    isTherapist: true,
  },
  {
    id: "railey",
    name: "Railey Lowrey, OTA",
    title: "Occupational Therapy Assistant",
    bio: "Railey brings genuine compassion, curiosity, and dedication to every patient interaction. Guided by her faith, she is committed to serving others with excellence and creating a lasting, positive impact within the community.",
    isTherapist: true,
  },
];

// Insurance providers - Updated list
const insuranceProviders = [
  "Self-Pay Cash Services",
  "BCBS of TX",
  "Tricare/Triwest",
  "Medicare (Traditional)",
  "Humana",
  "Cigna Products",
];

// Services data - Only OT for now
const services = [
  {
    id: "ot",
    title: "Occupational Therapy (OT)",
    shortTitle: "OT",
    image: OT_IMAGE,
    tagline: "How Occupational Therapy at Grace in Motion can help:",
    benefits: [
      "Hand and upper extremity therapy for injuries, pain, and post-surgical recovery",
      "Support for building skills for play, learning, and daily routines",
      "Support for moms and babies with recovery, bonding, and feeding",
      "Help managing chronic pain to improve daily function",
      "Treatment for muscle tightness, stiffness, and pain",
    ],
  },
];

// Testimonials data - Only OT related
const testimonials = [
  {
    id: 1,
    name: "Jennifer M.",
    role: "Parent in Nacogdoches",
    rating: 5,
    text: "Grace in Motion Rehab has been the best occupational therapy clinic for our family. Our son has made incredible progress with his fine motor skills and sensory processing. The pediatric therapists are patient, caring, and truly understand children with developmental delays. Best OT in Nacogdoches!",
    service: "Pediatric Occupational Therapy",
  },
  {
    id: 2,
    name: "Linda W.",
    role: "Caregiver in Diboll",
    rating: 5,
    text: "My mother needed rehabilitation after her stroke last year. We found Grace in Motion Rehab and couldn't be happier. Celina and her team provided exceptional occupational therapy—professional yet warm. Mom has regained so much independence through their services.",
    service: "Occupational Therapy",
  },
  {
    id: 3,
    name: "María G.",
    role: "Paciente en Nacogdoches",
    rating: 5,
    text: "Excelente clínica de terapia ocupacional en Nacogdoches. El equipo de Grace in Motion me ayudó a recuperarme completamente después de mi cirugía de mano. Son muy profesionales, hablan español, y siempre me hicieron sentir bienvenida. ¡La mejor rehabilitación en East Texas!",
    service: "Terapia Ocupacional",
  },
];

// Navigation Component
const Navigation = ({ isScrolled, mobileMenuOpen, setMobileMenuOpen }) => {
  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
    setMobileMenuOpen(false);
  };

  return (
    <header
      data-testid="navigation-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? "bg-dark/95 backdrop-blur-md header-scrolled" : "bg-dark/90 backdrop-blur-sm"
      }`}
    >
      <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          <a
            href="#home"
            onClick={(e) => {
              e.preventDefault();
              scrollToSection("home");
            }}
            className="flex-shrink-0"
            data-testid="logo-link"
          >
            <img src={LOGO} alt="Grace in Motion Rehab" className="h-14 w-auto" />
          </a>

          <div className="hidden md:flex items-center space-x-8">
            {["Home", "Services", "About", "Contact"].map((item) => (
              <button
                key={item}
                onClick={() => scrollToSection(item.toLowerCase())}
                className="text-warm-cream/90 hover:text-warm-cream font-medium nav-link transition-smooth"
                data-testid={`nav-${item.toLowerCase()}`}
              >
                {item}
              </button>
            ))}
            <a
              href="tel:9362215505"
              className="flex items-center gap-2 text-warm-wood hover:text-warm-beige transition-smooth"
              data-testid="nav-phone"
            >
              <Phone size={18} />
              <span className="font-medium">(936) 221-5505</span>
            </a>
          </div>

          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="md:hidden text-warm-cream p-2"
            data-testid="mobile-menu-toggle"
          >
            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {mobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="md:hidden py-4 border-t border-warm-cream/20"
            data-testid="mobile-menu"
          >
            <div className="flex flex-col space-y-4">
              {["Home", "Services", "About", "Contact"].map((item) => (
                <button
                  key={item}
                  onClick={() => scrollToSection(item.toLowerCase())}
                  className="text-warm-cream/90 hover:text-warm-cream font-medium py-2 text-left"
                  data-testid={`mobile-nav-${item.toLowerCase()}`}
                >
                  {item}
                </button>
              ))}
              <a
                href="tel:9362215505"
                className="flex items-center gap-2 text-warm-wood hover:text-warm-beige py-2"
                data-testid="mobile-nav-phone"
              >
                <Phone size={18} />
                <span className="font-medium">(936) 221-5505</span>
              </a>
            </div>
          </motion.div>
        )}
      </nav>
    </header>
  );
};

// Hero Section with Ken Burns Effect
const HeroSection = () => {
  return (
    <section
      id="home"
      data-testid="hero-section"
      className="relative min-h-[90vh] flex items-center justify-center overflow-hidden"
    >
      <div 
        className="absolute inset-0 hero-kenburns"
        style={{
          backgroundImage: `url(${HERO_IMAGE})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      />
      <div className="absolute inset-0 hero-overlay"></div>
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <h1
            className="text-3xl sm:text-4xl lg:text-5xl font-bold text-warm-cream tracking-tight mb-6"
            style={{ textShadow: "2px 2px 8px rgba(0,0,0,0.5)" }}
          >
            Grace in Motion Rehab
          </h1>
          <p
            className="text-base sm:text-lg text-warm-cream/90 max-w-3xl mx-auto mb-10"
            style={{ textShadow: "1px 1px 4px rgba(0,0,0,0.5)" }}
          >
            Occupational Therapy across the lifespan 
            with a focus on meaningful outcomes, whole-person care, and genuine connection.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Button
              onClick={() => document.getElementById("contact").scrollIntoView({ behavior: "smooth" })}
              className="bg-warm-wood hover:bg-warm-wood-dark text-white px-8 py-6 text-lg rounded-full transition-smooth"
              data-testid="hero-appointment-btn"
            >
              Request an Appointment
              <ArrowRight className="ml-2" size={20} />
            </Button>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

// Who We Are Section
const WhoWeAreSection = () => {
  return (
    <section id="who-we-are" data-testid="who-we-are-section" className="section-padding bg-warm-cream">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid md:grid-cols-2 gap-12 items-center">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <p className="text-sm uppercase tracking-[0.2em] font-semibold text-warm-wood mb-4">
              Who We Are
            </p>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-dark tracking-tight mb-6">
              About Grace in Motion Rehab
            </h2>
            <div className="text-dark/80 leading-relaxed space-y-4">
              <p>
                Grace in Motion Rehab is a locally owned outpatient therapy clinic in Nacogdoches, Texas, 
                providing Occupational Therapy for both children and adults.
              </p>
              <p>
                At Grace in Motion Therapy, our mission is grounded in faith and expressed through compassionate, 
                holistic care. We are dedicated to empowering each individual to reach their fullest potential 
                through personalized therapy that honors their unique journey.
              </p>
              <p className="font-medium text-warm-wood">
                Our goal is to foster independence, enhance quality of life, and uplift every person we are privileged to serve.
              </p>
            </div>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="relative"
          >
            <img
              src={WHO_WE_ARE_IMAGE}
              alt="Therapy session at Grace in Motion Rehab in Nacogdoches, Texas"
              className="rounded-lg shadow-xl w-full h-auto max-h-[500px] object-cover"
            />
            <div className="absolute -bottom-6 -right-6 bg-warm-wood text-white p-6 rounded-lg hidden lg:block">
              <div className="flex items-center gap-3">
                <Heart size={32} className="text-warm-beige" />
                <div>
                  <p className="font-bold text-lg">Faith-Based Care</p>
                  <p className="text-warm-cream/80 text-sm">Whole-Person Approach</p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// Services Section
const otHighlights = [
  {
    id: "building-skills",
    eyebrow: "For Children",
    title: "Building Skills for Play, Learning & Daily Routines",
    image: BUILDING_SKILLS_IMAGE,
    shortDesc: "Help managing chronic pain to improve daily function",
    longDesc:
      "We support children in developing the foundational skills they need for play, school, and everyday life. Through purposeful, play-based therapy we work on fine motor coordination, handwriting, sensory processing, attention, self-regulation, and independence with daily routines like dressing, eating, and self-care.",
    bullets: [
      "Fine motor & handwriting development",
      "Sensory processing & self-regulation",
      "Play-based learning and social skills",
      "Independence with dressing, feeding, and grooming",
      "Visual-motor and attention skills for school",
    ],
  },
  {
    id: "chronic-pain",
    eyebrow: "For Adults",
    title: "Managing Chronic Pain to Improve Daily Function",
    image: CHRONIC_PAIN_IMAGE,
    shortDesc: "Help managing chronic pain to improve daily function",
    longDesc:
      "Persistent pain shouldn't take you away from the activities you love. Our occupational therapy approach to chronic pain combines movement, education, ergonomic strategies, and graded activity so you can return to work, household tasks, hobbies, and meaningful routines with greater ease and confidence.",
    bullets: [
      "Personalized pain management strategies",
      "Joint protection & energy conservation techniques",
      "Ergonomic guidance for work and home",
      "Graded return to meaningful activities",
      "Education on pacing, posture, and self-care",
    ],
  },
];

const ServicesSection = () => {
  const [openHighlight, setOpenHighlight] = useState(null);

  const handleRequestAppointment = () => {
    const current = active;
    setOpenHighlight(null);
    setTimeout(() => {
      if (current) {
        window.dispatchEvent(
          new CustomEvent("prefillAppointment", {
            detail: { service: "OT", message: `Interested in: ${current.title}` },
          })
        );
      }
      const el = document.getElementById("contact");
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 150);
  };

  const active = otHighlights.find((h) => h.id === openHighlight) || null;

  return (
    <section id="services" data-testid="services-section" className="section-padding bg-warm-muted">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <p className="text-sm uppercase tracking-[0.2em] font-semibold text-warm-wood mb-4">
            What We Do
          </p>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-dark tracking-tight mb-4">
            Our Services
          </h2>
        </motion.div>

        <div className="grid md:grid-cols-1 max-w-3xl mx-auto gap-8 mb-12">
          {services.map((service, index) => (
            <motion.div
              key={service.id}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              className="bg-white rounded-lg overflow-hidden shadow-md card-hover transition-smooth"
              data-testid={`service-card-${service.id}`}
            >
              <div className="relative h-56 sm:h-64">
                <img
                  src={service.image}
                  alt={service.title}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 service-card-overlay flex items-end">
                  <div className="p-4">
                    <span className="bg-warm-wood text-white px-3 py-1 rounded-full text-sm font-medium">
                      {service.shortTitle}
                    </span>
                  </div>
                </div>
              </div>
              <div className="p-6 sm:p-8">
                <h3 className="text-xl sm:text-2xl font-bold text-dark mb-2">{service.title}</h3>
                <p className="text-warm-wood text-sm font-medium mb-4">{service.tagline}</p>
                <ul className="space-y-2">
                  {service.benefits.map((benefit, i) => (
                    <li key={i} className="flex items-start gap-2 text-dark/80 text-sm">
                      <Check size={16} className="text-warm-wood mt-0.5 flex-shrink-0" />
                      <span>{benefit}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </motion.div>
          ))}
        </div>

        {/* OT Highlights Gallery */}
        <div className="grid sm:grid-cols-2 gap-6 max-w-5xl mx-auto mb-12" data-testid="ot-highlights">
          {otHighlights.map((h, idx) => (
            <motion.button
              key={h.id}
              type="button"
              onClick={() => setOpenHighlight(h.id)}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: idx * 0.1 }}
              className="relative rounded-lg overflow-hidden shadow-md group text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-warm-wood focus-visible:ring-offset-2"
              data-testid={`highlight-${h.id}`}
              aria-label={`Learn more about ${h.title}`}
            >
              <img
                src={h.image}
                alt={h.title}
                className="w-full h-64 object-cover transition-transform duration-700 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-dark/80 via-dark/30 to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-5">
                <p className="text-xs uppercase tracking-[0.18em] text-warm-beige mb-1">{h.eyebrow}</p>
                <h4 className="text-white text-lg font-bold leading-tight mb-2">{h.title}</h4>
                <span className="inline-flex items-center gap-1 text-warm-beige text-xs font-semibold uppercase tracking-wider opacity-90 group-hover:opacity-100 transition-opacity">
                  Learn more <ArrowRight size={14} />
                </span>
              </div>
            </motion.button>
          ))}
        </div>

        <motion.p
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="text-center text-dark/80 max-w-3xl mx-auto font-medium"
        >
          We proudly serve individuals across the lifespan, from pediatrics to adults, 
          adapting care to meet each stage of life and level of need.
        </motion.p>
      </div>

      {/* Highlight Detail Dialog */}
      <Dialog open={!!openHighlight} onOpenChange={(o) => !o && setOpenHighlight(null)}>
        <DialogContent
          className="max-w-2xl p-0 overflow-hidden bg-white"
          data-testid="highlight-dialog"
        >
          {active && (
            <>
              <div className="relative h-56 sm:h-64">
                <img src={active.image} alt={active.title} className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-dark/70 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-5">
                  <p className="text-xs uppercase tracking-[0.18em] text-warm-beige mb-1">
                    {active.eyebrow}
                  </p>
                </div>
              </div>
              <div className="p-6 sm:p-8">
                <DialogHeader className="text-left">
                  <DialogTitle className="text-2xl font-bold text-dark leading-tight">
                    {active.title}
                  </DialogTitle>
                  <DialogDescription className="text-dark/80 text-sm leading-relaxed pt-2">
                    {active.longDesc}
                  </DialogDescription>
                </DialogHeader>

                <ul className="space-y-2 mt-5">
                  {active.bullets.map((b, i) => (
                    <li key={i} className="flex items-start gap-2 text-dark/80 text-sm">
                      <Check size={16} className="text-warm-wood mt-0.5 flex-shrink-0" />
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>

                <DialogFooter className="mt-6 sm:justify-start">
                  <Button
                    onClick={handleRequestAppointment}
                    className="bg-warm-wood hover:bg-warm-wood/90 text-white px-6 py-3 rounded-full font-semibold"
                    data-testid="highlight-dialog-cta"
                  >
                    Solicitar cita <ArrowRight size={16} className="ml-2" />
                  </Button>
                </DialogFooter>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
};

// About Us / Team Section
const AboutSection = () => {
  return (
    <section id="about" data-testid="about-section" className="py-12 sm:py-16 bg-warm-cream">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-8"
        >
          <p className="text-sm uppercase tracking-[0.2em] font-semibold text-warm-wood mb-2">
            About Us
          </p>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-dark tracking-tight">
            Meet Our Team
          </h2>
        </motion.div>

        <Tabs defaultValue="celina" className="w-full" data-testid="team-tabs">
          <TabsList className="w-full flex justify-center gap-4 sm:gap-6 bg-transparent mb-6">
            {teamMembers.map((member) => (
              <TabsTrigger
                key={member.id}
                value={member.id}
                className="px-2 py-2 text-dark/70 font-medium text-xs sm:text-sm data-[state=active]:text-warm-wood data-[state=active]:border-b-2 data-[state=active]:border-warm-wood rounded-none bg-transparent hover:text-warm-wood transition-smooth whitespace-nowrap"
                data-testid={`team-tab-${member.id}`}
              >
                {member.name.split(',')[0]}
              </TabsTrigger>
            ))}
          </TabsList>

          {teamMembers.map((member) => (
            <TabsContent key={member.id} value={member.id} className="team-tab-content">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className={member.isTherapist ? "bg-white p-6 rounded-lg shadow-md max-w-2xl mx-auto" : "max-w-2xl mx-auto py-4"}
                data-testid={`team-content-${member.id}`}
              >
                <div className="text-center">
                  <h3 className="text-xl font-bold text-dark mb-1">{member.name}</h3>
                  <p className="text-warm-wood font-medium mb-3 text-sm">{member.title}</p>
                  <p className="text-dark/80 leading-relaxed text-sm">{member.bio}</p>
                </div>
              </motion.div>
            </TabsContent>
          ))}
        </Tabs>
      </div>
    </section>
  );
};

// Testimonials Section
const TestimonialsSection = () => {
  return (
    <section id="testimonials" data-testid="testimonials-section" className="section-padding bg-warm-muted">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <p className="text-sm uppercase tracking-[0.2em] font-semibold text-warm-wood mb-4">
            Patient Reviews
          </p>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-dark tracking-tight mb-4">
            What Our Patients Say
          </h2>
        </motion.div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {testimonials.map((testimonial, index) => (
            <motion.div
              key={testimonial.id}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              className="bg-white p-6 rounded-lg shadow-md card-hover transition-smooth relative"
              data-testid={`testimonial-${testimonial.id}`}
            >
              <Quote className="absolute top-4 right-4 text-warm-beige/50" size={32} />
              
              <div className="flex items-center gap-1 mb-4">
                {[...Array(testimonial.rating)].map((_, i) => (
                  <Star key={i} size={18} className="fill-warm-wood text-warm-wood" />
                ))}
              </div>
              
              <p className="text-dark/80 text-sm leading-relaxed mb-4 italic">
                "{testimonial.text}"
              </p>
              
              <div className="border-t border-warm-beige pt-4">
                <p className="font-semibold text-dark">{testimonial.name}</p>
                <p className="text-warm-wood text-sm">{testimonial.role}</p>
                <p className="text-dark/50 text-xs mt-1">{testimonial.service}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Insurance Section
const InsuranceSection = () => {
  return (
    <section id="insurance" data-testid="insurance-section" className="py-12 bg-warm-cream">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center"
        >
          <p className="text-sm uppercase tracking-[0.2em] font-semibold text-warm-wood mb-2">
            Insurance
          </p>
          <h2 className="text-xl sm:text-2xl font-bold text-dark tracking-tight mb-4">
            Accepted Insurance Providers
          </h2>
          <p className="text-dark/70 mb-6">
            We accept a variety of insurance plans to serve our community.
          </p>
        </motion.div>

        <div className="flex flex-wrap justify-center gap-4 mb-8">
          {insuranceProviders.map((provider, index) => (
            <div
              key={index}
              className="px-6 py-3 bg-white rounded-full shadow-sm text-dark font-medium"
            >
              {provider}
            </div>
          ))}
        </div>

        <p className="text-center text-dark/70 text-sm">
          If you have questions about your coverage, please contact our office and we will be happy to assist you.
          <br />
          <a href="tel:9362215505" className="text-warm-wood font-medium hover:underline">
            (936) 221-5505
          </a>
        </p>
      </div>
    </section>
  );
};

// Contact Section with Form
const ContactSection = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    service: "",
    message: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    const handler = (e) => {
      const { service, message } = e.detail || {};
      setFormData((prev) => ({
        ...prev,
        service: service || prev.service,
        message: message || prev.message,
      }));
      setTimeout(() => {
        const nameInput = document.getElementById("name");
        if (nameInput) nameInput.focus({ preventScroll: true });
      }, 600);
    };
    window.addEventListener("prefillAppointment", handler);
    return () => window.removeEventListener("prefillAppointment", handler);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.service) {
      toast.error("Please select a service of interest.");
      return;
    }
    
    setIsSubmitting(true);

    try {
      await axios.post(`${API}/appointments`, formData);
      toast.success("Appointment request submitted successfully! We will contact you soon.");
      setFormData({ name: "", email: "", phone: "", service: "", message: "" });
    } catch (error) {
      console.error("Error submitting form:", error);
      toast.error("There was an error submitting your request. Please try again or call us directly.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  return (
    <section id="contact" data-testid="contact-section" className="section-padding bg-warm-muted">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <p className="text-sm uppercase tracking-[0.2em] font-semibold text-warm-wood mb-4">
            Contact + Access
          </p>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-dark tracking-tight mb-4">
            Grace in Motion Rehab
          </h2>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-12">
          {/* Contact Form */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            <div className="bg-white p-8 rounded-lg shadow-md">
              <h3 className="text-xl font-bold text-dark mb-3">Request an Appointment</h3>
              <div
                className="flex flex-wrap items-center gap-x-4 gap-y-2 text-xs sm:text-sm text-dark/70 mb-6 pb-5 border-b border-warm-beige/60"
                data-testid="form-trust-strip"
              >
                <span className="inline-flex items-center gap-1.5">
                  <Clock size={14} className="text-warm-wood" />
                  <span className="font-medium text-dark">Same-day appointments</span>
                </span>
                <span className="hidden sm:inline text-warm-beige">•</span>
                <span className="inline-flex items-center gap-1.5">
                  <Heart size={14} className="text-warm-wood" />
                  <span>Response within 24 hours</span>
                </span>
                <span className="hidden sm:inline text-warm-beige">•</span>
                <span className="inline-flex items-center gap-1.5">
                  <Check size={14} className="text-warm-wood" />
                  <span>Insurance accepted</span>
                </span>
              </div>
              <form onSubmit={handleSubmit} className="space-y-5" data-testid="appointment-form">
                <div>
                  <Label htmlFor="name" className="text-dark font-medium">Full Name *</Label>
                  <Input
                    id="name"
                    name="name"
                    type="text"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    placeholder="Your full name"
                    className="mt-1 border-warm-beige focus:border-warm-wood"
                    data-testid="form-name"
                  />
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="email" className="text-dark font-medium">Email *</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      placeholder="your@email.com"
                      className="mt-1 border-warm-beige focus:border-warm-wood"
                      data-testid="form-email"
                    />
                  </div>
                  <div>
                    <Label htmlFor="phone" className="text-dark font-medium">Phone *</Label>
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleChange}
                      required
                      placeholder="(936) 555-0123"
                      className="mt-1 border-warm-beige focus:border-warm-wood"
                      data-testid="form-phone"
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="service" className="text-dark font-medium">Service of Interest *</Label>
                  <Select
                    value={formData.service}
                    onValueChange={(value) => setFormData((prev) => ({ ...prev, service: value }))}
                  >
                    <SelectTrigger className="mt-1 border-warm-beige" data-testid="form-service">
                      <SelectValue placeholder="Select a service" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="OT">Occupational Therapy</SelectItem>
                      <SelectItem value="Other">Not Sure / Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="message" className="text-dark font-medium">Message (Optional)</Label>
                  <Textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    placeholder="Tell us about your needs or any questions you have..."
                    rows={4}
                    className="mt-1 border-warm-beige focus:border-warm-wood"
                    data-testid="form-message"
                  />
                </div>
                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-warm-wood hover:bg-warm-wood-dark text-white py-6 rounded-full transition-smooth"
                  data-testid="form-submit"
                >
                  {isSubmitting ? "Submitting..." : "Request an Appointment"}
                </Button>
              </form>
            </div>
          </motion.div>

          {/* Contact Info & Map */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="space-y-8"
          >
            <div className="bg-white p-8 rounded-lg shadow-md">
              <h3 className="text-xl font-bold text-dark mb-6">Visit Us</h3>
              <div className="space-y-4">
                <div className="flex items-start gap-4">
                  <MapPin className="text-warm-wood mt-1 flex-shrink-0" size={24} />
                  <div>
                    <p className="font-medium text-dark">Address</p>
                    <p className="text-dark/80">
                      3316 N. University Drive, Suite A<br />
                      Nacogdoches, TX 75965
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <Phone className="text-warm-wood mt-1 flex-shrink-0" size={24} />
                  <div>
                    <p className="font-medium text-dark">Phone</p>
                    <a
                      href="tel:9362215505"
                      className="text-warm-wood hover:underline"
                      data-testid="contact-phone"
                    >
                      (936) 221-5505
                    </a>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <Mail className="text-warm-wood mt-1 flex-shrink-0" size={24} />
                  <div>
                    <p className="font-medium text-dark">Fax</p>
                    <p className="text-dark/80">(936) 221-5183</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <Clock className="text-warm-wood mt-1 flex-shrink-0" size={24} />
                  <div>
                    <p className="font-medium text-dark">Hours</p>
                    <p className="text-dark/80">
                      Monday - Friday: 8:00 AM - 5:00 PM<br />
                      Saturday - Sunday: Closed
                    </p>
                  </div>
                </div>
              </div>
              
              {/* Social Media Links */}
              <div className="mt-6 pt-6 border-t border-warm-beige">
                <p className="font-medium text-dark mb-3">Follow Us</p>
                <div className="flex items-center gap-4">
                  <a
                    href={SOCIAL_LINKS.facebook}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 text-dark/70 hover:text-warm-wood transition-smooth"
                    data-testid="contact-facebook"
                  >
                    <Facebook size={24} />
                    <span className="text-sm">Facebook</span>
                  </a>
                  <a
                    href={SOCIAL_LINKS.instagram}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 text-dark/70 hover:text-warm-wood transition-smooth"
                    data-testid="contact-instagram"
                  >
                    <Instagram size={24} />
                    <span className="text-sm">Instagram</span>
                  </a>
                </div>
              </div>
            </div>

            {/* Google Map */}
            <div className="map-container" data-testid="google-map">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3399.5!2d-94.655!3d31.623!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2s3316%20N%20University%20Dr%20Suite%20A%2C%20Nacogdoches%2C%20TX%2075965!5e0!3m2!1sen!2sus!4v1234567890"
                width="100%"
                height="300"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Grace in Motion Rehab Location"
              ></iframe>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// Footer
const Footer = () => {
  return (
    <footer data-testid="footer" className="bg-dark text-warm-cream py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid md:grid-cols-2 gap-8 items-center">
          <div>
            <img src={LOGO} alt="Grace in Motion Rehab" className="h-16 w-auto mb-4" />
            <p className="text-warm-cream/70 text-sm mb-4">
              Serving Nacogdoches and surrounding communities
            </p>
            {/* Social Media Links */}
            <div className="flex items-center gap-4">
              <a
                href={SOCIAL_LINKS.facebook}
                target="_blank"
                rel="noopener noreferrer"
                className="text-warm-cream/70 hover:text-warm-wood transition-smooth"
                data-testid="footer-facebook"
                aria-label="Facebook"
              >
                <Facebook size={24} />
              </a>
              <a
                href={SOCIAL_LINKS.instagram}
                target="_blank"
                rel="noopener noreferrer"
                className="text-warm-cream/70 hover:text-warm-wood transition-smooth"
                data-testid="footer-instagram"
                aria-label="Instagram"
              >
                <Instagram size={24} />
              </a>
            </div>
          </div>
          <div className="text-center md:text-right">
            <p className="text-warm-cream/90 mb-2">
              3316 N. University Drive, Suite A<br />
              Nacogdoches, TX 75965
            </p>
            <a
              href="tel:9362215505"
              className="text-warm-wood hover:text-warm-beige font-medium"
              data-testid="footer-phone"
            >
              (936) 221-5505
            </a>
          </div>
        </div>
        <div className="mt-8 pt-8 border-t border-warm-cream/20 text-center">
          <p className="text-warm-cream/50 text-sm">
            © {new Date().getFullYear()} Grace in Motion Rehab. All rights reserved.
          </p>
        </div>
      </div>
    </footer>
  );
};

// Main App Component
function App() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Inject the ElevenLabs Conversational AI widget after 30 seconds
  useEffect(() => {
    const timer = setTimeout(() => {
      // Avoid double-injecting on hot reload
      if (document.querySelector("elevenlabs-convai")) return;

      const widget = document.createElement("elevenlabs-convai");
      widget.setAttribute("agent-id", "x8xjLC1AE04TC6s2vuMC");
      document.body.appendChild(widget);

      if (!document.querySelector('script[data-elevenlabs="true"]')) {
        const script = document.createElement("script");
        script.src = "https://unpkg.com/@elevenlabs/convai-widget-embed";
        script.async = true;
        script.dataset.elevenlabs = "true";
        document.body.appendChild(script);
      }
    }, 30000);

    return () => clearTimeout(timer);
  }, []);

  return (
    <div className="App font-montserrat">
      <Toaster position="top-right" richColors />
      <Navigation
        isScrolled={isScrolled}
        mobileMenuOpen={mobileMenuOpen}
        setMobileMenuOpen={setMobileMenuOpen}
      />
      <main>
        <HeroSection />
        <WhoWeAreSection />
        <ServicesSection />
        <AboutSection />
        <TestimonialsSection />
        <InsuranceSection />
        <ContactSection />
      </main>
      <Footer />

      {/* ElevenLabs Conversational AI Widget — injected after 30s by useEffect above */}
    </div>
  );
}

export default App;
